import { useParams, useSearchParams, Link, useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RuleBicolor } from '@audiq/ui';
import { api } from '../lib/api';
import { ApiClientError } from '../lib/api/_request';
import { PersonaTab } from './flow-detail/PersonaTab';
import { QuestionsTab } from './flow-detail/QuestionsTab';
import { OutcomesTab } from './flow-detail/OutcomesTab';
import { PreviewTab } from './flow-detail/PreviewTab';
import { FlowBuilder } from './flow-detail/FlowBuilder';
import { VersionsTab } from './flow-detail/VersionsTab';
import { TestSessionsTab } from './flow-detail/TestSessionsTab';

const TABS = [
  'persona',
  'questions',
  'outcomes',
  'builder',
  'preview',
  'versions',
  'test-sessions',
] as const;
type Tab = (typeof TABS)[number];

export function FlowDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const tab = (search.get('tab') as Tab) || 'persona';
  const setTab = (t: Tab) => {
    search.set('tab', t);
    setSearch(search, { replace: true });
  };

  const flowQuery = useQuery({
    queryKey: ['flow', id],
    queryFn: () => api.getFlow(id!),
    enabled: Boolean(id),
  });

  const publishMut = useMutation({
    mutationFn: () => api.publishFlow(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flow', id] }),
  });

  if (flowQuery.isLoading) return <p style={{ color: 'var(--mute)' }}>Carico…</p>;
  if (flowQuery.isError) {
    return (
      <p style={{ color: 'var(--danger)' }}>
        Flow non trovato.{' '}
        <Link to="/flows" style={{ color: 'var(--primary-2)' }}>
          Torna alla lista
        </Link>
      </p>
    );
  }
  if (!flowQuery.data) return null;
  const flow = flowQuery.data;

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mono mb-2" style={{ color: 'var(--mute)' }}>
        <Link to="/flows" className="hover:underline">
          Configurazione · Flow
        </Link>{' '}
        / {flow.slug}
      </div>
      <div className="flex items-baseline justify-between">
        <h2 className="serif text-4xl" style={{ color: 'var(--ink)' }}>
          {flow.name}
        </h2>
        <div className="flex items-center gap-2">
          {flow.is_active ? (
            <span
              className="mono rounded-full px-2 py-0.5"
              style={{
                background: 'color-mix(in oklab, var(--primary) 20%, transparent)',
                color: 'var(--primary-2)',
              }}
            >
              attivo
            </span>
          ) : flow.archived_at ? (
            <span
              className="mono rounded-full px-2 py-0.5"
              style={{ background: 'var(--soft)', color: 'var(--mute)' }}
            >
              archiviato
            </span>
          ) : (
            <span
              className="mono rounded-full px-2 py-0.5"
              style={{
                background: 'color-mix(in oklab, var(--accent) 20%, transparent)',
                color: 'var(--ink)',
              }}
            >
              draft
            </span>
          )}
          <button
            type="button"
            onClick={() => publishMut.mutate()}
            disabled={
              publishMut.isPending || Boolean(flow.archived_at) || flow.questions.length === 0
            }
            className="rounded-md px-3 py-1.5 text-sm disabled:opacity-50"
            style={{ background: 'var(--primary)', color: '#fff' }}
            title={flow.questions.length === 0 ? 'Aggiungi almeno una domanda per pubblicare' : ''}
          >
            {publishMut.isPending ? 'Pubblico…' : 'Pubblica'}
          </button>
        </div>
      </div>

      <RuleBicolor className="my-6" />

      <div
        className="mb-6 inline-flex flex-wrap rounded-md border"
        style={{ borderColor: 'var(--line)', background: 'var(--paper)' }}
      >
        {TABS.map((t, idx) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className="px-4 py-2 text-sm transition"
            style={{
              background: tab === t ? 'var(--soft)' : 'transparent',
              color: tab === t ? 'var(--primary-2)' : 'var(--ink)',
              fontWeight: tab === t ? 600 : 400,
              borderInlineEnd: idx < TABS.length - 1 ? '1px solid var(--line)' : 'none',
            }}
          >
            {tabLabel(t, flow.questions.length, flow.outcomes.length)}
          </button>
        ))}
      </div>

      {publishMut.isError && (
        <p className="mb-4 text-sm" style={{ color: 'var(--danger)' }}>
          {publishMut.error instanceof ApiClientError
            ? publishMut.error.payload.error
            : 'Errore pubblicazione'}
        </p>
      )}

      {tab === 'persona' && <PersonaTab flow={flow} />}
      {tab === 'questions' && <QuestionsTab flow={flow} />}
      {tab === 'outcomes' && <OutcomesTab flow={flow} />}
      {tab === 'builder' && <FlowBuilder flow={flow} />}
      {tab === 'preview' && <PreviewTab flow={flow} />}
      {tab === 'versions' && <VersionsTab flow={flow} />}
      {tab === 'test-sessions' && <TestSessionsTab flow={flow} />}

      <div className="mt-12 mono" style={{ color: 'var(--mute)' }}>
        <button
          type="button"
          onClick={() => {
            if (
              confirm(
                `Eliminare "${flow.name}"? Verrà archiviato (può essere ripristinato dal DB).`,
              )
            ) {
              api
                .deleteFlow(flow.id)
                .then(() => navigate('/flows'))
                .catch(() => alert('Errore di eliminazione'));
            }
          }}
          className="hover:underline"
          style={{ color: 'var(--danger)' }}
        >
          Archivia questo flow
        </button>
      </div>
    </div>
  );
}

function tabLabel(t: Tab, qCount: number, oCount: number): string {
  switch (t) {
    case 'persona':
      return 'Persona';
    case 'questions':
      return `Domande (${qCount})`;
    case 'outcomes':
      return `Esiti (${oCount})`;
    case 'builder':
      return 'Builder visuale';
    case 'preview':
      return 'Anteprima';
    case 'versions':
      return 'Versioni';
    case 'test-sessions':
      return 'Test sessions';
  }
}
